import { client } from "../../amplify-client";

function normalizeValue(value) {
  return String(value || "").trim();
}

function buildUserChatKey(userId, chatPk) {
  return `${normalizeValue(userId)}#${normalizeValue(chatPk)}`;
}

function getHiddenMessageModel() {
  return client?.models?.UserHiddenMessage;
}

export async function listHiddenMessageSkForUserChat({ userId, chatPk }) {
  const normalizedUserId = normalizeValue(userId);
  const normalizedChatPk = normalizeValue(chatPk);
  if (!normalizedUserId || !normalizedChatPk) return new Set();
  const hiddenModel = getHiddenMessageModel();
  if (!hiddenModel || typeof hiddenModel.list !== "function") {
    // Fail-open until backend schema is deployed in this environment.
    return new Set();
  }

  const userChatKey = buildUserChatKey(normalizedUserId, normalizedChatPk);
  let nextToken = undefined;
  const hiddenMessageSk = new Set();

  try {
    do {
      const resp = await hiddenModel.list({
        filter: { user_chat_key: { eq: userChatKey } },
        limit: 200,
        nextToken,
      });

      const { data, errors } = resp ?? {};
      if (errors?.length) {
        throw new Error(errors.map((e) => e.message).join(", "));
      }

      (data || []).forEach((entry) => {
        const messageSk = normalizeValue(entry?.message_sk);
        if (messageSk) hiddenMessageSk.add(messageSk);
      });

      nextToken = resp?.nextToken;
    } while (nextToken);
  } catch (error) {
    console.warn("Hidden message lookup failed; defaulting to visible messages.", error);
    return new Set();
  }

  return hiddenMessageSk;
}

export async function listHiddenMessageEntriesForUser(userId) {
  const normalizedUserId = normalizeValue(userId);
  if (!normalizedUserId) return [];

  const hiddenModel = getHiddenMessageModel();
  if (!hiddenModel || typeof hiddenModel.list !== "function") {
    return [];
  }

  let nextToken = undefined;
  const entries = [];

  try {
    do {
      const resp = await hiddenModel.list({
        filter: { user_id: { eq: normalizedUserId } },
        limit: 200,
        nextToken,
      });

      const { data, errors } = resp ?? {};
      if (errors?.length) {
        throw new Error(errors.map((e) => e.message).join(", "));
      }

      (data || []).forEach((entry) => {
        const chatId = normalizeValue(entry?.chat_id);
        const messageSk = normalizeValue(entry?.message_sk);
        if (!chatId || !messageSk) return;
        entries.push({
          chatId,
          messageSk,
          hiddenAt: entry?.hidden_at || null,
        });
      });

      nextToken = resp?.nextToken;
    } while (nextToken);
  } catch (error) {
    console.warn("Hidden message lookup failed; defaulting to visible messages.", error);
    return [];
  }

  entries.sort((a, b) => String(b.hiddenAt || "").localeCompare(String(a.hiddenAt || "")));
  return entries;
}

export async function hideMessageForUser({ userId, chatPk, messageSk }) {
  const normalizedUserId = normalizeValue(userId);
  const normalizedChatPk = normalizeValue(chatPk);
  const normalizedMessageSk = normalizeValue(messageSk);

  if (!normalizedUserId || !normalizedChatPk || !normalizedMessageSk) {
    throw new Error("Missing userId, chatPk, or messageSk for hide message.");
  }

  const userChatKey = buildUserChatKey(normalizedUserId, normalizedChatPk);
  const hiddenModel = getHiddenMessageModel();
  if (!hiddenModel || typeof hiddenModel.get !== "function" || typeof hiddenModel.create !== "function") {
    throw new Error("Message hide is unavailable until backend schema updates are deployed.");
  }

  const getResp = await hiddenModel.get({
    user_chat_key: userChatKey,
    message_sk: normalizedMessageSk,
  });

  if (getResp?.errors?.length) {
    throw new Error(getResp.errors.map((e) => e.message).join(", "));
  }

  if (getResp?.data) {
    return getResp.data;
  }

  const createResp = await hiddenModel.create({
    user_chat_key: userChatKey,
    message_sk: normalizedMessageSk,
    user_id: normalizedUserId,
    chat_id: normalizedChatPk,
    hidden_at: new Date().toISOString(),
  });

  if (createResp?.errors?.length) {
    throw new Error(createResp.errors.map((e) => e.message).join(", "));
  }

  return createResp?.data ?? null;
}

export async function unhideMessageForUser({ userId, chatPk, messageSk }) {
  const normalizedUserId = normalizeValue(userId);
  const normalizedChatPk = normalizeValue(chatPk);
  const normalizedMessageSk = normalizeValue(messageSk);

  if (!normalizedUserId || !normalizedChatPk || !normalizedMessageSk) {
    throw new Error("Missing userId, chatPk, or messageSk for unhide message.");
  }

  const hiddenModel = getHiddenMessageModel();
  if (!hiddenModel || typeof hiddenModel.delete !== "function") {
    throw new Error("Message unhide is unavailable until backend schema updates are deployed.");
  }

  const deleteResp = await hiddenModel.delete({
    user_chat_key: buildUserChatKey(normalizedUserId, normalizedChatPk),
    message_sk: normalizedMessageSk,
  });

  if (deleteResp?.errors?.length) {
    throw new Error(deleteResp.errors.map((e) => e.message).join(", "));
  }

  return true;
}
